import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { RailSection, RailCheckboxRow } from './CalendarRail';
import { useCalendarTags } from '../../hooks/useCalendar';
import { isHexColor } from '../../lib/calendar/tagTint';

/** Rows "Show more tags" can list — mirrors the server's VOCABULARY_LIMIT. */
const VOCABULARY_LIMIT = 50;
/** The Untagged row's tick colour: neutral, never a tag's. */
const UNTAGGED_COLOR = '#8A837E';

/**
 * "My calendars" (#681): a calendar is a tag. Which tags are listed (plan D1):
 *
 *   1. Untagged — pinned first, whenever the range has untagged events or the
 *      row is unticked. At launch it is the whole section: every event starts
 *      untagged, so this is the first impression, and hiding it is explicit.
 *   2. the tags on the viewer's events in the range (`tagFacets`, computed by
 *      the server BEFORE the filter, so an unticked tag keeps its row), with
 *      in-range counts;
 *   3. unticked tags with nothing in range, so they can be ticked again;
 *   4. "Show more tags" — the viewer's other calendar tags, no counts (all-time
 *      counts would mislead next to in-range ones).
 *
 * Ticking is server-side filtering (the page rebuilds the events query); the
 * selection is remembered as what is hidden (D2). Colour follows #679's rule:
 * a row is the tag's colour, which is the colour its events' chips take.
 */
export function MyCalendars({
    ready, facets = [], untaggedCount = 0, hidden, degraded = false,
    onToggleTag, onToggleUntagged, onShowAll, onPrune,
}) {
    const [moreOpen, setMoreOpen] = useState(false);
    const hiddenSet = useMemo(() => new Set(hidden.tagIds), [hidden.tagIds]);
    const facetIds = useMemo(() => new Set(facets.map(f => f.tag_id)), [facets]);
    const hiddenOutOfView = useMemo(() => hidden.tagIds.filter(id => !facetIds.has(id)), [hidden.tagIds, facetIds]);

    const vocabulary = useCalendarTags({ enabled: moreOpen || hiddenOutOfView.length > 0 });
    const vocab = useMemo(() => vocabulary.data || [], [vocabulary.data]);
    const vocabById = useMemo(() => new Map(vocab.map(t => [t.tag_id, t])), [vocab]);

    // A hidden id that is no longer a tag (deleted, merged away) is dropped once
    // the full vocabulary has been seen — never from a capped, partial list, and
    // never from a cached one: the vocabulary may predate a tag created since
    // (staleTime), so an unknown id first triggers a refetch and is pruned only if
    // the answer fetched AFTER that still lacks it.
    const pruneAskedAt = useRef(0);
    const { isSuccess: vocabReady, isFetching: vocabFetching, dataUpdatedAt, refetch } = vocabulary;
    useEffect(() => {
        if (!vocabReady || vocab.length >= VOCABULARY_LIMIT) return;
        if (!hiddenOutOfView.some(id => !vocabById.has(id))) {
            pruneAskedAt.current = 0;
            return;
        }
        if (!pruneAskedAt.current) {
            pruneAskedAt.current = Date.now();
            refetch?.();
            return;
        }
        if (vocabFetching || !(dataUpdatedAt > pruneAskedAt.current)) return;
        pruneAskedAt.current = 0;
        onPrune?.(new Set([...vocabById.keys(), ...facetIds]));
    }, [vocabReady, vocabFetching, dataUpdatedAt, refetch, vocab.length, hiddenOutOfView, vocabById, facetIds, onPrune]);

    if (!ready) return null;

    const anyHidden = hidden.untagged || hidden.tagIds.length > 0;
    const showUntagged = untaggedCount > 0 || hidden.untagged;
    const outOfViewRows = hiddenOutOfView.map(id => vocabById.get(id)).filter(Boolean);
    const listed = new Set([...facetIds, ...hiddenOutOfView]);
    const moreRows = vocab.filter(t => !listed.has(t.tag_id));
    const firstImpression = facets.length === 0 && hiddenOutOfView.length === 0;
    const colorOf = (t) => (isHexColor(t.color) ? t.color : undefined);

    return (
        <RailSection
            title="My calendars"
            action={anyHidden && !degraded ? (
                <button
                    type="button"
                    onClick={onShowAll}
                    className="text-[11px] font-medium text-[#4D11F6] hover:underline"
                    data-testid="my-calendars-show-all"
                >
                    Show all
                </button>
            ) : null}
        >
            <div data-testid="my-calendars">
                {degraded && (
                    <p className="mb-1.5 px-2 text-[11.5px] leading-snug text-[#92400E]" role="status" data-testid="my-calendars-degraded">
                        Tags couldn't load — showing everything.
                    </p>
                )}
                {showUntagged && (
                    <RailCheckboxRow
                        label="Untagged"
                        title="Events with no tag"
                        checked={!hidden.untagged}
                        onChange={onToggleUntagged}
                        color={UNTAGGED_COLOR}
                        count={untaggedCount}
                        disabled={degraded}
                        testId="my-calendars-untagged"
                    />
                )}
                {facets.map(t => (
                    <RailCheckboxRow
                        key={t.tag_id}
                        label={t.name}
                        checked={!hiddenSet.has(t.tag_id)}
                        onChange={(visible) => onToggleTag(t.tag_id, visible)}
                        color={colorOf(t)}
                        count={t.count}
                        disabled={degraded}
                        testId={`my-calendars-tag-${t.tag_id}`}
                    />
                ))}
                {outOfViewRows.map(t => (
                    <RailCheckboxRow
                        key={t.tag_id}
                        label={t.name}
                        title="No events in this range"
                        checked={false}
                        onChange={(visible) => onToggleTag(t.tag_id, visible)}
                        color={colorOf(t)}
                        disabled={degraded}
                        testId={`my-calendars-tag-${t.tag_id}`}
                    />
                ))}
                {firstImpression && !degraded && (
                    <p className="mt-1 px-2 text-[11.5px] leading-snug text-[#A29B95]" data-testid="my-calendars-empty">
                        Tags you put on meetings appear here as calendars, so you can show or hide them.
                    </p>
                )}
                <button
                    type="button"
                    onClick={() => setMoreOpen(o => !o)}
                    aria-expanded={moreOpen}
                    className="mt-1 flex items-center gap-1 px-2 h-7 text-[11.5px] font-medium text-[#57504B] hover:text-[#0C0A09]"
                    data-testid="my-calendars-more"
                >
                    {moreOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    Show more tags
                </button>
                {moreOpen && (
                    <div data-testid="my-calendars-more-list">
                        {vocabulary.isLoading && <p className="px-2 text-[11.5px] text-[#A29B95]">Loading…</p>}
                        {vocabulary.isError && <p className="px-2 text-[11.5px] text-[#A3382B]">Couldn't load your tags.</p>}
                        {vocabulary.isSuccess && moreRows.length === 0 && (
                            <p className="px-2 text-[11.5px] text-[#A29B95]" data-testid="my-calendars-more-empty">No other calendar tags.</p>
                        )}
                        {moreRows.map(t => (
                            <RailCheckboxRow
                                key={t.tag_id}
                                label={t.name}
                                title="No events in this range"
                                checked={!hiddenSet.has(t.tag_id)}
                                onChange={(visible) => onToggleTag(t.tag_id, visible)}
                                color={colorOf(t)}
                                disabled={degraded}
                                testId={`my-calendars-more-${t.tag_id}`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </RailSection>
    );
}
